/**
 * RESPONSIBILITY: one ministry's own page, with the pencil that edits that ministry alone.
 *
 * The pencil opens the ministries list narrowed to this row - see `focus` - exactly as an
 * event's page does. The whole list from here would be the same document and practically
 * useless: somebody who arrived from the Children page wants Children, not eight ministries
 * to find it among. The words every ministry page shares keep their own pencil.
 *
 * The row is looked up again inside the section BY ID, not by the address. The address is
 * made from the title, so an editor retyping the title would otherwise make the page
 * forget which ministry it was and show "not here" under the dialog they are typing in.
 *
 * An address that matches nothing still gets the "not here" page, and keeps the whole-list
 * pencil: there is no row to narrow to, which is exactly the situation.
 */
import { idOf, rowById } from '@framework/core';
import { SectionName, type EventRow } from '@app/sections';
import { cms, SectionUpdate } from '../cms';
import { MinistryPage, bySlugOrId } from '../site';

export function MinistryOnePage({ slug, events }: { slug: string; events: EventRow[] }) {
  // Read once out here only to know WHICH row to narrow the pencil to.
  const known = bySlugOrId(cms.get(SectionName.Ministries).data?.ministries ?? [], slug);
  const focus = known ? { field: 'ministries', rowId: idOf(known) } : undefined;

  return (
    <main>
      <SectionUpdate name={SectionName.MinistryPage} pencil="bottom-right">
        {(p) => (
          <SectionUpdate name={SectionName.Ministries} focus={focus}>
            {(c) => {
              const list = c.ministries ?? [];
              const m = known ? rowById(list, idOf(known)) ?? known : bySlugOrId(list, slug);
              return (
                <MinistryPage
                  m={m}
                  c={p}
                  events={events
                    .filter((e) => m?.eventTag && (e.categories ?? []).includes(m.eventTag))
                    .slice(0, 3)}
                />
              );
            }}
          </SectionUpdate>
        )}
      </SectionUpdate>
    </main>
  );
}
